import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { createNotification } from "@/lib/createNotification";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Plus, Trash2, CheckCircle2, XCircle, Receipt, FileText, Filter, Paperclip, Download, Pencil } from "lucide-react";
import { ExpenseReceiptsManager } from "@/components/controller/ExpenseReceiptsManager";
import { uploadExpenseAttachments } from "@/hooks/useExpenseAttachments";
import { AccountSelect } from "@/components/controller/AccountSelect";
import { useChartOfAccounts } from "@/hooks/useChartOfAccounts";
import { toast } from "sonner";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesAlert } from "@/components/shared/UnsavedChangesAlert";

import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const LEGACY_CATEGORIES: Record<string, string> = {
  labor: "Labor", material: "Material", equipment: "Equipment", travel: "Travel",
  permits: "Permits & Fees", subcontractor: "Subcontractor", fuel: "Fuel", tools: "Tools", other: "Other",
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending:     { label: "Pending",     className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  approved:    { label: "Approved",    className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  rejected:    { label: "Rejected",    className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  reimbursed:  { label: "Reimbursed", className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
};

const EMPTY_FORM = {
  description: "",
  amount: "",
  expense_date: new Date().toISOString().split("T")[0],
  account_id: null as string | null,
  vendor_name: "",
  notes: "",
  receiptFiles: [] as File[],
  removeReceipt: false,
};

interface Props {
  projectId: string;
}

export const ProjectExpensesTab = ({ projectId }: Props) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { can, isAdmin } = usePermissions();
  const qc = useQueryClient();
  const navigate = useTabNavigate();
  const { data: accounts } = useChartOfAccounts();

  const accountMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts ?? []) {
      map.set(a.id, a.account_number ? `${a.account_number} — ${a.name}` : a.name);
    }
    return map;
  }, [accounts]);

  const getAccountLabel = (row: any) => {
    if (row.account_id && accountMap.has(row.account_id)) return accountMap.get(row.account_id)!;
    if (row.category) return LEGACY_CATEGORIES[row.category] ?? row.category;
    return "—";
  };

  const canManage = isAdmin || can("edit_work_orders") || can("approve_bills");
  const canAdd    = canManage || can("edit_work_orders") || can("submit_expenses");

  const [addOpen, setAddOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: expenses, isLoading } = useQuery({
    queryKey: ["project_expenses", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expenses")
        .select("*")
        .eq("project_id", projectId)
        .order("expense_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const expenseIds = useMemo(() => (expenses ?? []).map((e: any) => e.id), [expenses]);

  const { data: attachmentsByExpense } = useQuery({
    queryKey: ["project_expense_attachments_by_project", projectId, expenseIds.length],
    enabled: expenseIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expense_attachments" as any)
        .select("id, expense_id, file_name, file_path, file_size, mime_type, created_at")
        .in("expense_id", expenseIds);
      if (error) throw error;
      const map = new Map<string, any[]>();
      for (const att of (data ?? []) as any[]) {
        const arr = map.get(att.expense_id) ?? [];
        arr.push(att);
        map.set(att.expense_id, arr);
      }
      return map;
    },
  });

  const [previewExpense, setPreviewExpense] = useState<any | null>(null);
  const [previewItems, setPreviewItems] = useState<{ name: string; path: string; url?: string; error?: string }[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openReceiptPreview = async (row: any) => {
    const items: { name: string; path: string }[] = [];
    if (row.receipt_path) items.push({ name: "Receipt (legacy)", path: row.receipt_path });
    for (const att of attachmentsByExpense?.get(row.id) ?? []) {
      items.push({ name: att.file_name, path: att.file_path });
    }
    setPreviewExpense(row);
    setPreviewItems(items.map((i) => ({ ...i })));
    setPreviewLoading(true);
    const signed = await Promise.all(
      items.map(async (i) => {
        const { data, error } = await supabase.storage
          .from("expense-receipts")
          .createSignedUrl(i.path, 3600);
        return { ...i, url: data?.signedUrl, error: error?.message };
      })
    );
    setPreviewItems(signed);
    setPreviewLoading(false);
  };

  const filtered = useMemo(() => {
    let rows = expenses ?? [];
    if (categoryFilter !== "all") rows = rows.filter((r: any) => r.account_id === categoryFilter || r.category === categoryFilter);
    if (statusFilter !== "all") rows = rows.filter((r: any) => r.status === statusFilter);
    return rows;
  }, [expenses, categoryFilter, statusFilter]);

  const total = useMemo(() =>
    (expenses ?? []).reduce((s: number, r: any) => s + Number(r.amount), 0), [expenses]);

  const approvedTotal = useMemo(() =>
    (expenses ?? []).filter((r: any) => r.status === "approved" || r.status === "reimbursed")
      .reduce((s: number, r: any) => s + Number(r.amount), 0), [expenses]);

  const handleAdd = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");

    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { data: inserted, error } = await supabase.from("project_expenses").insert({
        company_id: companyId,
        project_id: projectId,
        description: form.description.trim(),
        amount,
        expense_date: form.expense_date,
        account_id: form.account_id || null,
        vendor_name: form.vendor_name.trim() || null,
        notes: form.notes.trim() || null,
        submitted_by: user?.id,
        status: "pending",
      }).select("id").single();
      if (error) throw error;

      // Upload receipts if provided
      if (form.receiptFiles.length > 0 && inserted?.id) {
        const res = await uploadExpenseAttachments({
          expense_id: inserted.id,
          company_id: companyId as unknown as string,
          files: form.receiptFiles,
        });
        if (res.failed.length > 0) {
          toast.error(`Expense saved. ${res.failed.length} receipt(s) failed to upload.`);
        }
      }

      toast.success("Expense added");
      createNotification({
        entity_type: "project", entity_id: projectId, project_id: projectId,
        type: "expense_added", title: "Expense Added",
        message: `${form.description} — $${parseFloat(form.amount).toFixed(2)}`,
        priority: "normal", action_url: `/controller/projects/${projectId}?tab=expenses`,
      });
      setForm({ ...EMPTY_FORM });
      setAddOpen(false);
      invalidateFinancials(qc, { projectId });
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
      qc.invalidateQueries({ queryKey: ["project_expense_attachments_by_project", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const { error } = await supabase.from("project_expenses").update({
        status: "approved",
        approved_by: user?.id,
        approved_at: new Date().toISOString(),
      }).eq("id", id);
      if (error) throw error;
      toast.success("Expense approved");
      createNotification({
        entity_type: "project", entity_id: id, project_id: projectId,
        type: "expense_approved", title: "Expense Approved",
        message: "An expense has been approved.",
        priority: "normal", action_url: `/controller/projects/${projectId}?tab=expenses`,
      });
      invalidateFinancials(qc, { projectId });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleReject = async (id: string) => {
    try {
      const { error } = await supabase.from("project_expenses").update({ status: "rejected" }).eq("id", id);
      if (error) throw error;
      toast.success("Expense rejected");
      createNotification({
        entity_type: "project", entity_id: id, project_id: projectId,
        type: "expense_rejected", title: "Expense Rejected",
        message: "An expense has been rejected.",
        priority: "high", action_url: `/controller/projects/${projectId}?tab=expenses`,
      });
      invalidateFinancials(qc, { projectId });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from("project_expenses").delete().eq("id", id);
      if (error) throw error;
      toast.success("Expense removed");
      invalidateFinancials(qc, { projectId });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (row: any) => {
    setEditingExpense(row);
    setForm({
      description: row.description || "",
      amount: String(row.amount ?? ""),
      expense_date: row.expense_date || new Date().toISOString().split("T")[0],
      account_id: row.account_id || null,
      vendor_name: row.vendor_name || "",
      notes: row.notes || "",
      receiptFiles: [],
      removeReceipt: false,
    });
    setAddOpen(true);
  };

  const handleSave = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");

    setBusy(true);
    try {
      const { error } = await supabase.from("project_expenses").update({
        description: form.description.trim(),
        amount,
        expense_date: form.expense_date,
        account_id: form.account_id || null,
        vendor_name: form.vendor_name.trim() || null,
        notes: form.notes.trim() || null,
      }).eq("id", editingExpense.id);
      if (error) throw error;

      // Handle legacy receipt removal
      if (form.removeReceipt && editingExpense.receipt_path) {
        await supabase.storage.from("expense-receipts").remove([editingExpense.receipt_path]);
        await supabase.from("project_expenses").update({ receipt_path: null }).eq("id", editingExpense.id);
      }

      toast.success("Expense updated");
      setForm({ ...EMPTY_FORM });
      setEditingExpense(null);
      setAddOpen(false);
      qc.invalidateQueries({ queryKey: ["project_expenses", projectId] });
      qc.invalidateQueries({ queryKey: ["project_expense_attachments_by_project", projectId] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    setAddOpen(false);
    setEditingExpense(null);
    setForm({ ...EMPTY_FORM });
  };

  const isEditing = !!editingExpense;

  const isDirty = !!(
    form.description.trim() ||
    form.amount ||
    form.vendor_name.trim() ||
    form.notes.trim() ||
    form.account_id ||
    (form.receiptFiles && form.receiptFiles.length > 0) ||
    form.removeReceipt
  );
  const guard = useUnsavedChangesGuard(isDirty, closeDialog);



  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Total Expenses</p>
          <p className="font-semibold text-lg">{fmt(total)}</p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Approved / Reimbursed</p>
          <p className="font-semibold text-lg text-green-700 dark:text-green-400">{fmt(approvedTotal)}</p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Pending</p>
          <p className="font-semibold text-lg text-amber-700 dark:text-amber-400">
            {fmt((expenses ?? []).filter((r: any) => r.status === "pending").reduce((s: number, r: any) => s + Number(r.amount), 0))}
          </p>
        </div>
        <div className="border rounded-md p-3 space-y-0.5">
          <p className="text-xs text-muted-foreground">Count</p>
          <p className="font-semibold text-lg">{(expenses ?? []).length}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {canAdd && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Expense
          </Button>
        )}
        {filtered.length > 0 && <ExportToolbar entityType="expenses" data={filtered} filterDescription={`category: ${categoryFilter}, status: ${statusFilter}`} />}
        {isMobile ? (
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFiltersOpen(!filtersOpen)}>
            <Filter className="h-3.5 w-3.5 mr-1" /> Filters
            {(categoryFilter !== "all" || statusFilter !== "all") && <span className="ml-1 w-2 h-2 rounded-full bg-primary" />}
          </Button>
        ) : (
          <>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 h-8">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {(accounts ?? []).map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.account_number ? `${a.account_number} — ` : ""}{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-8">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="reimbursed">Reimbursed</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} expense{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Mobile filter panel */}
      {isMobile && filtersOpen && (
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="flex-1 h-8">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts</SelectItem>
              {(accounts ?? []).map(a => (
                <SelectItem key={a.id} value={a.id}>{a.account_number ? `${a.account_number} — ` : ""}{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 h-8">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="reimbursed">Reimbursed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No expenses recorded for this project.</p>
          {canAdd && (
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add First Expense
            </Button>
          )}
        </div>
      ) : isMobile ? (
        /* ── Mobile Card View ── */
        <div className="space-y-3">
          {filtered.map((row: any) => {
            const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
            const catLabel = getAccountLabel(row);
            const canDelete = canManage || (row.submitted_by === user?.id && row.status === "pending");
            return (
              <div key={row.id} className={cn("border rounded-lg p-3 space-y-2", row.status === "rejected" && "opacity-60")}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm truncate flex-1">{row.description}</span>
                  <Badge variant="outline" className={cn("text-[10px] ml-2 shrink-0", badge.className)}>{badge.label}</Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{format(new Date(row.expense_date), "MMM d, yyyy")}</span>
                  <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                  {row.vendor_name && <span>· {row.vendor_name}</span>}
                </div>
                <div className="text-lg font-bold">{fmt(Number(row.amount))}</div>
                {row.bill_id && (
                  <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary cursor-pointer" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                    Auto • Bill
                  </Badge>
                )}
                {row.notes && !row.bill_id && <p className="text-xs text-muted-foreground line-clamp-2">{row.notes}</p>}
                {(canManage || canDelete) && (
                  <div className="flex items-center gap-2 pt-1">
                    {row.status === "pending" && canManage && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleApprove(row.id)}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleReject(row.id)}><XCircle className="h-4 w-4" /></Button>
                      </>
                    )}
                    {canManage && !row.bill_id && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => openEdit(row)} title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive ml-auto" onClick={() => handleDelete(row.id)} disabled={deletingId === row.id}>
                        {deletingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop Table View ── */
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Vendor / Payee</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Receipt</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row: any) => {
                const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
                const catLabel = getAccountLabel(row);
                const canDelete = canManage || (row.submitted_by === user?.id && row.status === "pending");
                return (
                  <TableRow key={row.id} className={cn(row.status === "rejected" && "opacity-60")}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {format(new Date(row.expense_date), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.vendor_name ?? "—"}</TableCell>
                    <TableCell>
                      <div className="font-medium text-sm flex items-center gap-1">
                        {row.bill_id ? (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 cursor-pointer group" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                                  <FileText className="inline h-3.5 w-3.5 text-primary shrink-0 group-hover:text-primary/70 transition-colors" />
                                  <span className="group-hover:underline underline-offset-2">{row.description}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">View source bill</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          row.description
                        )}
                      </div>
                      {row.bill_id && (
                        <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary mt-0.5 cursor-pointer hover:bg-primary/20 transition-colors" onClick={() => navigate(`/controller/bills?billId=${row.bill_id}`)}>
                          Auto • Bill
                        </Badge>
                      )}
                      {row.notes && !row.bill_id && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{row.notes}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{fmt(Number(row.amount))}</TableCell>
                    <TableCell>
                      {(() => {
                        const atts = attachmentsByExpense?.get(row.id) ?? [];
                        const count = (row.receipt_path ? 1 : 0) + atts.length;
                        if (count === 0) return <span className="text-xs text-muted-foreground">—</span>;
                        return (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 relative"
                            title={count === 1 ? "View receipt" : `View ${count} receipts`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openReceiptPreview(row);
                            }}
                          >
                            <Paperclip className="h-3.5 w-3.5 text-primary" />
                            {count > 1 && (
                              <span className="absolute -top-0.5 -right-0.5 text-[9px] font-semibold bg-primary text-primary-foreground rounded-full h-3.5 min-w-3.5 px-1 flex items-center justify-center">
                                {count}
                              </span>
                            )}
                          </Button>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[10px]", badge.className)}>{badge.label}</Badge>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!row.bill_id && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(row)} title="Edit">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {row.status === "pending" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={() => handleApprove(row.id)} title="Approve"><CheckCircle2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => handleReject(row.id)} title="Reject"><XCircle className="h-4 w-4" /></Button>
                            </>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(row.id)} disabled={deletingId === row.id} title="Delete">
                              {deletingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add / Edit Expense Dialog */}
      <Dialog open={addOpen} onOpenChange={guard.guardOpenChange((v) => { if (!v) closeDialog(); else setAddOpen(v); })}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" dirty={isDirty} onRequestClose={guard.requestClose}>

          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> {isEditing ? "Edit Expense" : "Add Project Expense"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the expense details below." : "Record a direct project cost (labor, material, travel, permits, etc.)"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 min-w-0">
            <div className="space-y-1">
              <label className="text-sm font-medium">Description *</label>
              <Input
                placeholder="e.g. Permit fee — City of Austin"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Amount *</label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Date *</label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Account</label>
                <AccountSelect
                  value={form.account_id}
                  onValueChange={v => setForm(f => ({ ...f, account_id: v }))}
                  placeholder="Select account"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Vendor / Payee</label>
                <Input
                  placeholder="Who was paid"
                  value={form.vendor_name}
                  onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                placeholder="Additional details, reference number, etc."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <ExpenseReceiptsManager
              expenseId={isEditing ? editingExpense?.id : null}
              legacyReceiptPath={isEditing ? editingExpense?.receipt_path : null}
              legacyRemoved={form.removeReceipt}
              onClearLegacy={() => setForm(f => ({ ...f, removeReceipt: true }))}
              pendingFiles={form.receiptFiles}
              onPendingFilesChange={(files) => setForm(prev => ({ ...prev, receiptFiles: files }))}
              pasteActive={addOpen}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={guard.requestClose}>Cancel</Button>
            <Button onClick={isEditing ? handleSave : handleAdd} disabled={busy}>
              {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{isEditing ? "Saving…" : "Adding…"}</> : isEditing ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
          <UnsavedChangesAlert
            open={guard.confirmOpen}
            onKeepEditing={guard.keepEditing}
            onDiscard={guard.confirmDiscard}
          />
        </DialogContent>
      </Dialog>

      {/* Receipt preview dialog */}
      <Dialog open={!!previewExpense} onOpenChange={(v) => { if (!v) { setPreviewExpense(null); setPreviewItems([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Receipts</DialogTitle>
            <DialogDescription>
              {previewExpense?.description} — {previewExpense ? fmt(Number(previewExpense.amount)) : ""}
            </DialogDescription>
          </DialogHeader>

          {previewLoading && previewItems.every((i) => !i.url && !i.error) ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : previewItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No receipts attached.</p>
          ) : (
            <div className="space-y-4">
              {previewItems.map((item, idx) => (
                <div key={idx} className="border border-border rounded-md overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40">
                    <span className="text-sm font-medium truncate">{item.name}</span>
                    {item.url && (
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => window.open(item.url, "_blank")}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Open
                      </Button>
                    )}
                  </div>
                  <div className="bg-background">
                    {item.error ? (
                      <div className="p-4 text-sm text-destructive flex items-center gap-2">
                        <XCircle className="h-4 w-4" /> Could not load receipt: {item.error}
                      </div>
                    ) : !item.url ? (
                      <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                    ) : /\.(png|jpe?g|webp|gif)$/i.test(item.name) ? (
                      <img
                        src={item.url}
                        alt={item.name}
                        className="w-full max-h-[60vh] object-contain bg-muted/20"
                        onError={() => setPreviewItems((prev) => prev.map((p, i) => i === idx ? { ...p, error: "Image failed to load" } : p))}
                      />
                    ) : /\.pdf$/i.test(item.name) ? (
                      <iframe src={item.url} title={item.name} className="w-full h-[60vh]" />
                    ) : (
                      <div className="p-4 text-sm text-muted-foreground">
                        Preview not available for this file type. Use Open to view.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};
